import type { CourseGroupId } from '../types/content';

export type ContactTopicId = CourseGroupId | 'visa' | 'student' | 'other';
export type ContactStep = 'topic' | 'course' | 'location' | 'details';

export interface ContactField {
  id: string;
  label: string;
  type: 'select' | 'text';
  options?: string[];
  placeholder?: string;
}

export interface ContactTopic {
  id: ContactTopicId;
  label: string;
  text: string;
  /** The steps this topic needs, in order. */
  steps: ContactStep[];
  fields: ContactField[];
  submitLabel: string;
}

export const MONTH_OPTIONS = 'months';

// Each topic decides which steps and which extra questions the contact form shows.
export const contactTopics: ContactTopic[] = [
  {
    id: 'english',
    label: 'Learn English',
    text: 'General English, IELTS, Business English, evening classes and more.',
    steps: ['topic', 'course', 'location', 'details'],
    fields: [
      { id: 'Start month', label: 'When do you want to start?', type: 'select', options: [MONTH_OPTIONS] },
      { id: 'Course length', label: 'How long?', type: 'select', options: ['1 to 3 weeks', '4 to 8 weeks', '9 to 12 weeks', '13 to 24 weeks', 'More than 24 weeks', 'Not sure yet'] },
      { id: 'Pace', label: 'Your pace', type: 'select', options: ['Intensive', 'Semi-intensive', 'Not sure yet'] },
      { id: 'English level', label: 'Your English level', type: 'select', options: ['Beginner', 'Elementary', 'Intermediate', 'Upper-intermediate', 'Advanced', 'Not sure yet'] },
    ],
    submitLabel: 'Get my study plan',
  },
  {
    id: 'young',
    label: 'Camps & young learners',
    text: 'Summer and winter camps, academic camps and school group programmes.',
    steps: ['topic', 'course', 'location', 'details'],
    fields: [
      { id: 'Student age', label: 'Age of the student', type: 'select', options: ['6 to 9', '10 to 12', '13 to 15', '16 to 17'] },
      { id: 'Number of students', label: 'How many students?', type: 'select', options: ['One student', '2 to 5 students', 'A school group (6+)'] },
      { id: 'Season', label: 'Preferred season', type: 'select', options: ['Summer', 'Winter', 'Not sure yet'] },
    ],
    submitLabel: 'Ask about camp dates',
  },
  {
    id: 'professional',
    label: 'Professional courses',
    text: 'CELTA, teacher training, digital marketing and career qualifications.',
    steps: ['topic', 'course', 'location', 'details'],
    fields: [
      { id: 'Start month', label: 'When do you want to start?', type: 'select', options: [MONTH_OPTIONS] },
      { id: 'Experience', label: 'Your experience', type: 'select', options: ['New to the field', '1 to 3 years', 'More than 3 years'] },
      { id: 'Study mode', label: 'How do you want to study?', type: 'select', options: ['On campus', 'Online', 'Not sure yet'] },
    ],
    submitLabel: 'Ask about this course',
  },
  {
    id: 'short',
    label: 'Short courses',
    text: 'Seasonal courses of a few weeks in languages, marketing and AI.',
    steps: ['topic', 'course', 'location', 'details'],
    fields: [
      { id: 'Season', label: 'Preferred season', type: 'select', options: ['Summer', 'Winter', 'Not sure yet'] },
      { id: 'Course length', label: 'How long?', type: 'select', options: ['1 week', '2 weeks', '3 to 4 weeks', 'Not sure yet'] },
    ],
    submitLabel: 'Ask about short courses',
  },
  {
    id: 'visa',
    label: 'Visas & accommodation',
    text: 'Student visas, tourist visas, homestays, residences and apartments.',
    steps: ['topic', 'location', 'details'],
    fields: [
      { id: 'I need help with', label: 'I need help with', type: 'select', options: ['A student visa', 'A tourist visa', 'Accommodation', 'Visa and accommodation'] },
      { id: 'Arrival month', label: 'When do you arrive?', type: 'select', options: [MONTH_OPTIONS] },
    ],
    submitLabel: 'Send my question',
  },
  {
    id: 'student',
    label: "I'm a current student",
    text: 'Timetables, certificates, holidays, course changes or student support.',
    steps: ['topic', 'location', 'details'],
    fields: [{ id: 'Student ID', label: 'Student ID (optional)', type: 'text', placeholder: 'For example ES-00000' }],
    submitLabel: 'Contact Student Services',
  },
  {
    id: 'other',
    label: 'Something else',
    text: 'Partnerships, agents, media, careers or a general question.',
    steps: ['topic', 'details'],
    fields: [{ id: 'Organisation', label: 'Organisation (optional)', type: 'text' }],
    submitLabel: 'Send my message',
  },
];

export const stepLabels: Record<ContactStep, string> = {
  topic: 'Topic',
  course: 'Programme',
  location: 'Location',
  details: 'Your details',
};
